<script setup lang="ts">
import { computed } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import { useManagementStore } from '@/store/management'

const store = useManagementStore()

const entries = computed(() => store.comparisonEntries)

function totalsFor(entry: (typeof entries.value)[number]) {
  return store.areaIndicatorsByQuery(entry.areaId, entry.periodQuery)?.totals ?? null
}

// Categorias multilinha: nome da área + período.
const categories = computed(() => entries.value.map((e) => [e.areaName, e.periodLabel]))

const consumptionSeries = computed(() => [
  { name: 'Ponta', data: entries.value.map((e) => Number(totalsFor(e)?.consumo_ponta ?? 0)) },
  { name: 'Fora ponta', data: entries.value.map((e) => Number(totalsFor(e)?.consumo_fora_ponta ?? 0)) },
])

const hasCost = computed(() => entries.value.some((e) => totalsFor(e)?.custo_total != null))
const costSeries = computed(() => [
  { name: 'Custo ponta', data: entries.value.map((e) => Number(totalsFor(e)?.custo_ponta ?? 0)) },
  { name: 'Custo fora ponta', data: entries.value.map((e) => Number(totalsFor(e)?.custo_fora_ponta ?? 0)) },
])

function baseOptions(colors: string[], unit: string) {
  return {
    chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit' },
    plotOptions: { bar: { columnWidth: '50%', borderRadius: 2 } },
    colors,
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'left' },
    xaxis: { categories: categories.value },
    yaxis: {
      labels: {
        formatter: (v: number) =>
          unit === 'R$'
            ? `R$ ${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
            : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`,
      },
    },
    tooltip: {
      y: {
        formatter: (v: number) =>
          unit === 'R$'
            ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
            : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${unit}`,
      },
    },
  }
}

const consumptionOptions = computed(() => baseOptions(['#f59e0b', '#3b82f6'], 'kWh'))
const costOptions = computed(() => baseOptions(['#f59e0b', '#04304c'], 'R$'))

const fmt = (v: number | null | undefined, unit = '') =>
  v === null || v === undefined ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`
const money = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
</script>

<template>
  <div class="space-y-4">
    <div
      v-if="!entries.length"
      class="flex min-h-[260px] items-center justify-center border border-stroke bg-slate-50 text-center text-sm text-slate-400">
      Selecione uma área, escolha um período e clique em "+ Comparar área".<br />
      Você pode comparar a mesma área em períodos diferentes.
    </div>

    <template v-else>
      <!-- Chips das comparações -->
      <div class="flex flex-wrap gap-2">
        <span
          v-for="entry in entries"
          :key="entry.id"
          class="flex items-center gap-2 border border-stroke bg-white px-3 py-1.5 text-sm">
          <span class="font-medium text-slate-700">{{ entry.areaName }}</span>
          <span class="text-xs text-slate-400">{{ entry.periodLabel }}</span>
          <button type="button" class="text-slate-400 hover:text-red" @click="store.removeComparisonEntry(entry.id)">✕</button>
        </span>
      </div>

      <!-- Gráficos de colunas -->
      <div class="grid grid-cols-1 gap-4" :class="hasCost ? 'lg:grid-cols-2' : ''">
        <div class="border border-stroke bg-white p-4">
          <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Consumo (kWh)</h4>
          <VueApexCharts type="bar" height="300" :options="consumptionOptions" :series="consumptionSeries" />
        </div>
        <div v-if="hasCost" class="border border-stroke bg-white p-4">
          <h4 class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Custo de energia (R$)</h4>
          <VueApexCharts type="bar" height="300" :options="costOptions" :series="costSeries" />
        </div>
      </div>

      <!-- Tabela de valores -->
      <div class="overflow-x-auto border border-stroke bg-white">
        <table class="w-full text-left text-sm">
          <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th class="px-4 py-2.5">Área / período</th>
              <th class="px-4 py-2.5 text-right">Consumo total</th>
              <th class="px-4 py-2.5 text-right">Ponta</th>
              <th class="px-4 py-2.5 text-right">Fora ponta</th>
              <th class="px-4 py-2.5 text-right">Custo total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.id" class="border-t border-stroke">
              <td class="px-4 py-2.5">
                <span class="font-medium text-slate-700">{{ entry.areaName }}</span>
                <span class="ml-1 text-xs text-slate-400">{{ entry.periodLabel }}</span>
              </td>
              <td class="px-4 py-2.5 text-right text-base font-bold text-slate-900">{{ fmt(totalsFor(entry)?.consumo_total, 'kWh') }}</td>
              <td class="px-4 py-2.5 text-right text-slate-700">{{ fmt(totalsFor(entry)?.consumo_ponta) }}</td>
              <td class="px-4 py-2.5 text-right text-slate-700">{{ fmt(totalsFor(entry)?.consumo_fora_ponta) }}</td>
              <td class="px-4 py-2.5 text-right text-base font-bold text-primary">{{ money(totalsFor(entry)?.custo_total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
